import { Auth, Storage } from 'aws-amplify';
import config from '../config';

// API endpoint URL for the content management API
const API_ENDPOINT = config.api.url;

// Submit a solution with files
export async function submitSolution(title, authors, track, files) {
  try {
    // Get current authenticated user
    const user = await Auth.currentAuthenticatedUser();
    const userId = user.attributes.sub;
    const username = user.attributes.email || user.username;

    // The Cognito Identity Pool ID — different from the user-pool `sub`. This
    // is what Amplify embeds in the `private/<id>/...` S3 path. Reviewers need
    // it to fetch the file via the backend presigned-URL endpoint.
    let submitterIdentityId = null;
    try {
      const credentials = await Auth.currentCredentials();
      submitterIdentityId = credentials?.identityId || null;
    } catch (e) {
      console.warn('Could not resolve identityId; reviewer file fetch will fall back to bucket scan.', e);
    }

    // Generate a submission folder name with timestamp
    const timestamp = Date.now();
    const submissionFolder = `submissions/${timestamp}`;
    
    // Upload all files to S3 with improved error handling
    const uploadedFiles = [];
    const uploadErrors = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        // Validate file
        if (!file || !file.name) {
          throw new Error('Invalid file: missing name');
        }
        
        // Check if file is a proper File/Blob object
        if (!(file instanceof File) && !(file instanceof Blob)) {
          throw new Error(`Invalid file object: ${file.name} is not a File or Blob`);
        }
        
        // Sanitize filename to prevent issues
        const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const key = `${submissionFolder}/${sanitizedName}`;
        
        // Upload the file with simplified configuration for large files
        const uploadResult = await Storage.put(key, file, {
          contentType: file.type || 'application/octet-stream',
          level: 'private',
          progressCallback: (progress) => {
            // Call the file's progress callback if it exists
            if (file.onProgress) {
              file.onProgress(progress);
            }
          }
        });
        
        
        // Return file metadata
        uploadedFiles.push({
          name: file.name,
          sanitizedName: sanitizedName,
          size: file.size || 0,
          type: file.type || 'application/octet-stream',
          key: key,
          uploadResult: uploadResult,
          lastModified: file.lastModified || Date.now(),
          md5Hash: await calculateMD5(file)
        });
        
        // Mark file as uploaded
        if (file.onProgress) {
          file.onProgress({ loaded: file.size, total: file.size });
        }
        
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
        uploadErrors.push({
          fileName: file.name,
          error: error.message
        });
        
        // Mark file as having an error
        if (file.onError) {
          file.onError(error.message);
        }
      }
    }
    
    // Check if any files were uploaded successfully
    if (uploadedFiles.length === 0) {
      throw new Error('No files were uploaded successfully. Please check your files and try again.');
    }
    
    // Warn about failed uploads but continue with successful ones
    if (uploadErrors.length > 0) {
      console.warn('Some files failed to upload:', uploadErrors);
    }
    
    // Prepare submission data
    const submissionData = {
      userId,
      username,
      submitterIdentityId,
      title,
      authors, // Replace description with authors array
      track,
      files: uploadedFiles,
      submissionId: `sub_${timestamp}`,
      timestamp: new Date().toISOString(),
      uploadErrors: uploadErrors.length > 0 ? uploadErrors : undefined
    };
    
    
    // Call the API to save submission metadata
    const response = await fetch(`${API_ENDPOINT}submissions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submissionData),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API error: ${response.status} - ${errorText}`);
    }
    
    const result = await response.json();
    
    return {
      ...result,
      uploadedFiles: uploadedFiles.length,
      totalFiles: files.length,
      uploadErrors: uploadErrors
    };
    
  } catch (error) {
    console.error('Error submitting solution:', error);
    throw error;
  }
}

// Get user submissions
export async function getUserSubmissions() {
  try {
    // Get current authenticated user
    const user = await Auth.currentAuthenticatedUser();
    const userId = user.attributes.sub;
    
    
    // Call the API
    const response = await fetch(`${API_ENDPOINT}submissions/${userId}`);
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    return data.submissions || [];
  } catch (error) {
    console.error('Error fetching user submissions:', error);
    throw error;
  }
}

// Get a signed URL for the submitter's OWN file via Amplify (works because
// IAM grants each user access to their own `private/<their-id>/*` path).
export async function getFileUrl(key) {
  try {
    const url = await Storage.get(key, {
      level: 'private',
      expires: 3600 // URL expires in 1 hour
    });
    return url;
  } catch (error) {
    console.error('Error getting file URL:', error);
    throw error;
  }
}

// Get a signed URL for ANY submission's file via the backend (used by
// reviewers and admins, who can't read other users' `private/` paths).
// `fileName` may be the original or sanitized filename — both are matched.
export async function getSubmissionFileUrl(submissionId, fileName) {
  const url = `${API_ENDPOINT}submissions/file-url?submissionId=${encodeURIComponent(submissionId)}&file=${encodeURIComponent(fileName)}`;
  const resp = await fetch(url);
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Failed to get file URL: ${resp.status} ${text}`);
  }
  const data = await resp.json();
  return data.url;
}

// Test Storage configuration
export async function testStorageConfig() {
  try {
    const user = await Auth.currentAuthenticatedUser();
    
    // Try to list files in the user's private folder
    const files = await Storage.list('', { level: 'private' });
    
    return { success: true, user: user.attributes.sub, files };
  } catch (error) {
    console.error('Storage test failed:', error);
    return { success: false, error: error.message };
  }
}

// Calculate MD5 hash of a file
async function calculateMD5(file) {
  try {
    // Import crypto-js for MD5 calculation
    const CryptoJS = await import('crypto-js');
    
    // Read file as binary string to avoid encoding issues
    const reader = new FileReader();
    const binaryString = await new Promise((resolve, reject) => {
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsBinaryString(file);
    });
    
    // Convert binary string to WordArray
    const wordArray = CryptoJS.enc.Latin1.parse(binaryString);
    const hash = CryptoJS.MD5(wordArray);
    
    return hash.toString();
  } catch (error) {
    console.error('Error calculating MD5 hash:', error);
    return null;
  }
}
